import { z } from "zod";
import { FORMAS_PAGAMENTO } from "../dominio";
import { id } from "./comuns";

/** Campo vazio na query string ("inicio=") vale como não informado. */
const vazioComoAusente = (v: unknown) => (v === "" ? undefined : v);

/** Aceita "a,b,c" (query string) ou array; sempre devolve array sem vazios. */
const lista = <T extends z.ZodType>(item: T) =>
  z.preprocess(
    (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v),
    z.array(item).max(50),
  );

/** Data e hora no formato do campo datetime-local, no horário de São Paulo: "2026-10-26T08:00". */
const dataHoraLocal = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, { error: "Data e hora inválidas" });

export const STATUS_FILTRO_VENDA = ["VALIDA", "CANCELADA", "TODAS"] as const;
export type StatusFiltroVenda = (typeof STATUS_FILTRO_VENDA)[number];

/**
 * Filtros do Fechamento. Os mesmos valores valem para a tela, o CSV e o PDF, e ficam na URL
 * para salvar ou compartilhar uma visão.
 */
export const filtrosRelatorioSchema = z
  .object({
    evento: lista(id).optional(),
    operador: lista(id).optional(),
    caixa: lista(id).optional(),
    categoria: lista(id).optional(),
    produto: lista(id).optional(),
    pagamento: lista(z.enum(FORMAS_PAGAMENTO)).optional(),
    inicio: z.preprocess(vazioComoAusente, dataHoraLocal.optional()),
    fim: z.preprocess(vazioComoAusente, dataHoraLocal.optional()),
    status: z.preprocess(vazioComoAusente, z.enum(STATUS_FILTRO_VENDA).default("VALIDA")),
    /** Nº da venda (busca exata). */
    numero: z.preprocess(vazioComoAusente, z.coerce.number().int().min(1).max(2_000_000_000).optional()),
  })
  .refine((f) => !f.inicio || !f.fim || f.fim >= f.inicio, {
    error: "O fim do período não pode ser antes do início",
    path: ["fim"],
  });
export type FiltrosRelatorio = z.output<typeof filtrosRelatorioSchema>;

/** Filtros → query string (mesma ordem sempre, sem valores vazios nem o status padrão). */
export function filtrosParaBusca(f: Partial<FiltrosRelatorio>): string {
  const p = new URLSearchParams();
  const listas = ["evento", "operador", "caixa", "categoria", "produto", "pagamento"] as const;
  for (const chave of listas) {
    const valor = f[chave];
    if (valor?.length) p.set(chave, valor.join(","));
  }
  if (f.inicio) p.set("inicio", f.inicio);
  if (f.fim) p.set("fim", f.fim);
  if (f.status && f.status !== "VALIDA") p.set("status", f.status);
  if (f.numero) p.set("numero", String(f.numero));
  return p.toString();
}
